import { useEffect, useState } from 'react';
import { Plus, Download, Trash2, Receipt, X } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { api, formatCurrency, API_BASE } from '../lib/api';

const CATEGORIES = ['Salaries', 'Rent', 'Software', 'Marketing Ads', 'Office', 'Travel', 'Utilities', 'Professional Fees', 'Other'];

const Expenses = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [expenses, setExpenses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [banks, setBanks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ date: today, amount: '', vendor: '', category: 'Other', notes: '', company_id: '', bank_account_id: '' });
  const [filter, setFilter] = useState({ company_id: '', start_date: '', end_date: '' });
  const [busy, setBusy] = useState(false);
  const { isViewer } = useAuth();

  const fetchAll = () => {
    const params = {};
    if (filter.company_id) params.company_id = filter.company_id;
    if (filter.start_date) params.start_date = filter.start_date;
    if (filter.end_date) params.end_date = filter.end_date;
    api.get('/expenses', { params }).then(r => setExpenses(r.data));
  };

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data));
    api.get('/bank-accounts').then(r => setBanks(r.data));
  }, []);
  useEffect(fetchAll, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/expenses', { ...form, amount: parseFloat(form.amount) });
      setShowModal(false);
      setForm({ date: today, amount: '', vendor: '', category: 'Other', notes: '', company_id: '', bank_account_id: '' });
      fetchAll();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this expense? Bank balance will be reverted.')) return;
    await api.delete(`/expenses/${id}`);
    fetchAll();
  };

  const inputCls = "bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm w-full transition-colors";
  const labelCls = "block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2";

  const totalAmount = expenses.reduce((s, e) => s + Number(e.amount), 0);

  return (
    <Layout
      title="Expenses"
      action={
        <div className="flex gap-2">
          <button data-testid="export-expenses-csv" onClick={() => {
            const q = new URLSearchParams();
            if (filter.company_id) q.set('company_id', filter.company_id);
            if (filter.start_date) q.set('start_date', filter.start_date);
            if (filter.end_date) q.set('end_date', filter.end_date);
            window.open(`${API_BASE}/exports/expenses.csv?${q.toString()}`, '_blank');
          }} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-4 py-2 font-heading text-sm flex items-center transition-colors">
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Export CSV
          </button>
          {!isViewer && (
            <button data-testid="new-expense-button" onClick={() => setShowModal(true)} className="bg-maroon text-white hover:bg-maroon-hover px-6 py-3 font-heading text-base flex items-center">
              <Plus className="w-5 h-5 mr-2" strokeWidth={1} /> Record Expense
            </button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-3 gap-6 mb-6">
        <div className="bg-surface border border-border p-6">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Total</p>
          <p data-testid="expenses-total" className="font-numbers text-3xl text-maroon tabular-nums">{formatCurrency(totalAmount)}</p>
        </div>
        <div className="bg-surface border border-border p-6">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Count</p>
          <p data-testid="expenses-count" className="font-numbers text-3xl text-text-primary tabular-nums">{expenses.length}</p>
        </div>
        <div className="bg-surface border border-border p-6">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Avg / Expense</p>
          <p className="font-numbers text-3xl text-text-primary tabular-nums">{expenses.length > 0 ? formatCurrency(totalAmount / expenses.length) : '—'}</p>
        </div>
      </div>

      <div className="bg-surface border border-border p-6">
        <div className="flex flex-wrap gap-4 mb-6 items-end">
          <div>
            <label className={labelCls}>From</label>
            <input data-testid="expenses-start-date" type="date" value={filter.start_date} onChange={(e) => setFilter({ ...filter, start_date: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>To</label>
            <input data-testid="expenses-end-date" type="date" value={filter.end_date} onChange={(e) => setFilter({ ...filter, end_date: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Company</label>
            <select data-testid="expenses-company-filter" value={filter.company_id} onChange={(e) => setFilter({ ...filter, company_id: e.target.value })} className={inputCls}>
              <option value="">All</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.full_name || c.name}</option>)}
            </select>
          </div>
        </div>

        <table className="w-full">
          <thead>
            <tr>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Date</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Vendor</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Category</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Notes</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Amount</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-center w-12"></th>
            </tr>
          </thead>
          <tbody>
            {expenses.map(e => (
              <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-border hover:bg-cream transition-colors">
                <td className="py-3 font-body text-sm">{e.date}</td>
                <td className="py-3 font-body text-sm text-text-primary">{e.vendor || '—'}</td>
                <td className="py-3 font-body text-sm text-text-secondary">{e.category || '—'}</td>
                <td className="py-3 font-body text-sm text-text-secondary truncate max-w-xs">{e.notes || '—'}</td>
                <td className="py-3 font-numbers text-sm text-maroon text-right tabular-nums">{formatCurrency(e.amount)}</td>
                <td className="py-3 text-center">{!isViewer && <button onClick={() => remove(e.id)} className="text-text-secondary hover:text-maroon"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>}</td>
              </tr>
            ))}
            {expenses.length === 0 && <tr><td colSpan="6" className="py-12 text-center text-text-secondary font-body text-sm">No expenses recorded yet</td></tr>}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div data-testid="expense-modal" className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-cream border border-border w-[min(560px,95vw)]">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div>
                <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-0.5">— Outflow</p>
                <h2 className="font-heading text-lg font-medium flex items-center"><Receipt className="w-4 h-4 mr-2 text-maroon" strokeWidth={1.5} /> Record Expense</h2>
              </div>
              <button onClick={() => setShowModal(false)} className="text-text-secondary hover:text-maroon"><X className="w-4 h-4" strokeWidth={1.5} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Date</label>
                  <input data-testid="expense-date" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Amount (₹)</label>
                  <input data-testid="expense-amount" required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Vendor</label>
                  <input data-testid="expense-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={inputCls} placeholder="Who you paid" />
                </div>
                <div>
                  <label className={labelCls}>Category</label>
                  <select data-testid="expense-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Company</label>
                  <select data-testid="expense-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className={inputCls}>
                    <option value="">—</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.full_name || c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Bank Account</label>
                  <select data-testid="expense-bank" value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} className={inputCls}>
                    <option value="">—</option>
                    {banks.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Notes</label>
                <textarea data-testid="expense-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button type="button" onClick={() => setShowModal(false)} className="font-heading text-sm text-text-secondary hover:text-maroon px-4 py-2">Cancel</button>
                <button data-testid="expense-submit" disabled={busy} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm transition-colors disabled:opacity-50">{busy ? 'Saving…' : 'Save Expense'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Expenses;
